import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * RoleGuard - Protects routes/components based on platform role or org role
 * 
 * Props:
 * - requireSuperAdmin: boolean - only superadmins can access
 * - requireOrgAdmin: boolean - org admins (owner/admin) or superadmin can access
 * - requirePermission: string - specific permission key from role_permissions
 * - requireMenuAccess: string - menu key (dashboard, sales, operations, etc.)
 * - fallback: ReactNode - what to render if access denied (default: redirect to /dashboard)
 * - redirectTo: string - where to redirect if access denied
 */
const RoleGuard = ({
  children,
  requireSuperAdmin = false,
  requireOrgAdmin = false,
  requirePermission = null,
  requireMenuAccess = null,
  fallback = null,
  redirectTo = '/dashboard',
}) => {
  const { user, loading, profileLoading, isSuperAdmin, isOrgAdmin, hasPermission, canViewMenu } = useAuth();

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Check superadmin requirement
  if (requireSuperAdmin && !isSuperAdmin) {
    if (fallback) return fallback;
    return <Navigate to={redirectTo} replace />;
  }

  // Check org admin requirement
  if (requireOrgAdmin && !isOrgAdmin && !isSuperAdmin) {
    if (fallback) return fallback;
    return <Navigate to={redirectTo} replace />;
  }

  // Check specific permission
  if (requirePermission && !hasPermission(requirePermission)) {
    if (fallback) return fallback;
    return <Navigate to={redirectTo} replace />;
  }

  // Check menu access
  if (requireMenuAccess && !canViewMenu(requireMenuAccess)) {
    if (fallback) return fallback;
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default RoleGuard;
